import { ChevronDown } from 'lucide-react'
import React, { useState } from 'react'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from 'src/component/ui/drawer'
import { Input } from 'src/component/ui/input/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/component/ui/select'
import { WeightUnit } from 'src/dao/core-dao'
import { useMediaQuery } from 'src/hook/use-media-query'
import { cn } from 'src/lib/utils'

interface WeightFieldProps {
  value: string
  unit: WeightUnit
  onValueChange: (value: string) => void
  onUnitChange: (unit: WeightUnit) => void
  error?: string
  disabled?: boolean
}

const getUnitDisplayText = (unit: WeightUnit): string => {
  switch (unit) {
    case WeightUnit.KG:
      return 'kg'
    case WeightUnit.LB:
      return 'lb'
    default:
      return 'kg'
  }
}

export const WeightField = ({
  value,
  unit,
  onValueChange,
  onUnitChange,
  error,
  disabled,
}: WeightFieldProps): React.ReactElement => {
  const isMobile = useMediaQuery('(max-width: 556px)')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const handleUnitSelect = (selectedUnit: WeightUnit): void => {
    onUnitChange(selectedUnit)
    setIsDrawerOpen(false)
  }

  const unitOptions = [
    { value: WeightUnit.KG, label: 'kg' },
    { value: WeightUnit.LB, label: 'lb' },
  ]

  if (isMobile) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-roboto-condensed font-bold text-mist">Weight</label>
        <div className="flex">
          <Input
            size="sm"
            type="number"
            step="0.1"
            value={value || ''}
            placeholder="0"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onValueChange(e.target.value)}
            disabled={disabled}
            className="rounded-r-none border-r-0"
          />
          <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <DrawerTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                className={cn(
                  'flex h-9 w-24 items-center justify-between rounded-l-none rounded-r-md bg-secondary-gray px-3 py-2 text-sm',
                  'font-evogria font-normal text-light-gray uppercase',
                  'focus-visible:outline-none',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                <span className="line-clamp-1 text-sm font-evogria text-light-gray uppercase">{getUnitDisplayText(unit)}</span>
                <ChevronDown className="h-4 w-4 text-light-gray" />
              </button>
            </DrawerTrigger>
            <DrawerContent className="bg-secondary-gray">
              <DrawerHeader>
                <DrawerTitle className="font-roboto-condensed font-bold text-mist text-center">Select Unit</DrawerTitle>
              </DrawerHeader>
              <div className="px-4 pb-4 space-y-2">
                {unitOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleUnitSelect(option.value)}
                    className={cn(
                      'w-full text-left rounded-md bg-secondary-gray px-4 py-3 text-sm',
                      'font-evogria font-normal text-light-gray uppercase',
                      'hover:bg-primary-gray transition-colors',
                      unit === option.value && 'bg-primary-gray',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </DrawerContent>
          </Drawer>
        </div>
        {error && <p className="text-sm font-roboto-condensed text-danger">{error}</p>}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-roboto-condensed font-bold text-mist">Weight</label>
      <div className="flex">
        <Input
          size="sm"
          type="number"
          step="0.1"
          value={value || ''}
          placeholder="0"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onValueChange(e.target.value)}
          disabled={disabled}
          className="rounded-r-none border-r-0"
        />
        <Select value={unit} onValueChange={(val) => onUnitChange(val as WeightUnit)} disabled={disabled}>
          <SelectTrigger className="w-24 rounded-l-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={WeightUnit.KG}>
              <p className="text-sm font-evogria text-light-gray uppercase">kg</p>
            </SelectItem>
            <SelectItem value={WeightUnit.LB}>
              <p className="text-sm font-evogria text-light-gray uppercase">lb</p>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      {error && <p className="text-sm font-roboto-condensed text-danger">{error}</p>}
    </div>
  )
}
